import React, { useState } from "react";
import { SearchInput } from "@/components/ui/search-input";
import { useTranslation } from "react-i18next";
import { StatusFilter, SortFilter } from "@/components/ui/filter-dropdown";
import { TagFilter } from "@/components/ui/TagFilter";
import { Button } from "@/components/ui/button";
import { RotateCcw, Filter, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Database } from "@/integrations/supabase/types";
import { useUserTags } from "@/hooks/useTags";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

type ProcessingStatus = Database["public"]["Enums"]["processing_status"];
type SortBy = "newest" | "oldest" | "name-asc" | "name-desc";

interface ResponsiveFilterGroupProps {
  // Search
  searchTerm: string;
  onSearchChange: (value: string) => void;

  // Status Filter
  statusFilter: ProcessingStatus | "all";
  onStatusChange: (value: ProcessingStatus | "all") => void;

  // Sort Filter
  sortBy: SortBy;
  onSortChange: (value: SortBy) => void;

  // Tag Filter
  selectedTagIds?: string[];
  onTagsChange?: (tagIds: string[]) => void;
  userId?: string;
  showTagFilter?: boolean;

  // Layout & State
  className?: string;
  disabled?: boolean;

  // Reset functionality
  onResetAll?: () => void;
  showResetButton?: boolean;
}

export const ResponsiveFilterGroup: React.FC<ResponsiveFilterGroupProps> = ({
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusChange,
  sortBy,
  onSortChange,
  selectedTagIds = [],
  onTagsChange,
  userId,
  showTagFilter = false,
  className,
  disabled,
  onResetAll,
  showResetButton = true,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  // Load available tags for current user
  const { data: availableTags = [], isLoading: tagsLoading } = useUserTags({
    userId: userId || "",
    enabled: showTagFilter && !!userId,
  });

  // Check if any non-default filters are applied
  const hasActiveFilters = searchTerm.length > 0 || statusFilter !== "all" || sortBy !== "newest" || (selectedTagIds.length > 0);

  // Count active filters
  const activeFiltersCount = [
    searchTerm.length > 0,
    statusFilter !== "all",
    sortBy !== "newest",
    selectedTagIds.length > 0
  ].filter(Boolean).length;

  const handleResetAll = () => {
    onSearchChange("");
    onStatusChange("all");
    onSortChange("newest");
    if (onTagsChange) {
      onTagsChange([]);
    }
    if (onResetAll) {
      onResetAll();
    }
  };

  return (
    <div className={cn("space-y-4", className)}>
      {/* Desktop Version - показывается на экранах sm и больше */}
      <div className="hidden sm:block">
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <SearchInput
              value={searchTerm}
              onChange={onSearchChange}
              placeholder={t('dashboard.search_placeholder')}
              disabled={disabled}
              className="w-full"
            />
          </div>

          <div className="flex gap-2">
            <StatusFilter
              value={statusFilter}
              onValueChange={onStatusChange}
              disabled={disabled}
              className="w-[160px]"
            />

            <SortFilter
              value={sortBy}
              onValueChange={onSortChange}
              disabled={disabled}
              className="w-[170px]"
            />

            {showTagFilter && userId && onTagsChange && (
              <TagFilter
                availableTags={availableTags}
                selectedTagIds={selectedTagIds}
                onTagsChange={onTagsChange}
                disabled={disabled || tagsLoading}
                className="w-[200px]"
              />
            )}

            {showResetButton && hasActiveFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleResetAll}
                disabled={disabled}
                className="whitespace-nowrap"
              >
                <RotateCcw size={14} className="mr-2" />
                {t('dashboard.filters.reset')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Version - показывается только на маленьких экранах */}
      <div className="sm:hidden">
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <div className="relative">
            <CollapsibleTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-between transition-all duration-200 hover:shadow-sm relative z-20",
                  isOpen && "border-blue-200 bg-blue-50/50 dark:border-blue-700/50 dark:bg-blue-950/30 shadow-md"
                )}
                disabled={disabled}
              >
                <div className="flex items-center gap-2">
                  <Filter size={16} className={cn(
                    "transition-colors duration-200",
                    isOpen ? "text-blue-600" : "text-muted-foreground"
                  )} />
                  <span className={cn(
                    "transition-colors duration-200",
                    isOpen ? "text-blue-600" : "text-foreground"
                  )}>
                    {t('dashboard.filters.title')}
                  </span>
                  {hasActiveFilters && (
                    <div className={cn(
                      "min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center text-xs font-medium transition-all duration-200",
                      isOpen ? "bg-blue-600 text-white" : "bg-blue-500 text-white"
                    )}>
                      {activeFiltersCount}
                    </div>
                  )}
                </div>
                <div className={cn(
                  "transition-transform duration-200 ease-in-out",
                  isOpen ? "rotate-180" : "rotate-0"
                )}>
                  <ChevronDown size={16} className={cn(
                    "transition-colors duration-200",
                    isOpen ? "text-blue-600" : "text-muted-foreground"
                  )} />
                </div>
              </Button>
            </CollapsibleTrigger>

            <CollapsibleContent className={cn(
              "absolute top-full left-0 right-0 z-50 mt-2",
              "bg-card/95 backdrop-blur-sm border border-border rounded-lg shadow-lg",
              "p-4 space-y-3",
              "data-[state=open]:animate-in data-[state=closed]:animate-out",
              "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
              "data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2",
              "duration-200 ease-in-out"
            )}>
              {/* Search Input with slide animation */}
              <div className={cn(
                "transform transition-all duration-300 ease-out",
                isOpen ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"
              )}>
                <SearchInput
                  value={searchTerm}
                  onChange={onSearchChange}
                  placeholder={t('dashboard.search_placeholder')}
                  disabled={disabled}
                  className="w-full"
                />
              </div>

              {/* Filters with staggered animation */}
              <div className="space-y-2">
                <div className={cn(
                  "transform transition-all duration-300 ease-out delay-75",
                  isOpen ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"
                )}>
                  <StatusFilter
                    value={statusFilter}
                    onValueChange={onStatusChange}
                    disabled={disabled}
                    className="w-full"
                  />
                </div>

                <div className={cn(
                  "transform transition-all duration-300 ease-out delay-100",
                  isOpen ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"
                )}>
                  <SortFilter
                    value={sortBy}
                    onValueChange={onSortChange}
                    disabled={disabled}
                    className="w-full"
                  />
                </div>

                {/* Tag Filter with staggered animation */}
                {showTagFilter && userId && onTagsChange && (
                  <div className={cn(
                    "transform transition-all duration-300 ease-out delay-125",
                    isOpen ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"
                  )}>
                    <TagFilter
                      availableTags={availableTags}
                      selectedTagIds={selectedTagIds}
                      onTagsChange={onTagsChange}
                      disabled={disabled || tagsLoading}
                      className="w-full"
                    />
                  </div>
                )}

                {/* Reset Button with fade animation */}
                {showResetButton && hasActiveFilters && (
                  <div className={cn(
                    "transform transition-all duration-300 ease-out delay-150",
                    isOpen ? "translate-y-0 opacity-100 scale-100" : "translate-y-2 opacity-0 scale-95"
                  )}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleResetAll}
                      disabled={disabled}
                      className="w-full hover:bg-red-50 hover:border-red-200 hover:text-red-700 transition-colors duration-200"
                    >
                      <RotateCcw size={14} className="mr-2" />
                      {t('dashboard.filters.reset_all')}
                    </Button>
                  </div>
                )}
              </div>
            </CollapsibleContent>

            {/* Overlay backdrop when filters are open */}
            {isOpen && (
              <div
                className="fixed inset-0 bg-black/10 backdrop-blur-[1px] z-40"
                onClick={() => setIsOpen(false)}
                aria-hidden="true"
              />
            )}
          </div>
        </Collapsible>
      </div>

      {/* Active Filters Summary (for screen readers and visual clarity) */}
      {hasActiveFilters && (
        <div className="sr-only" aria-live="polite" aria-atomic="true">
          {t('dashboard.filters.active_filters_sr', {
            search: searchTerm,
            status: statusFilter,
            sort: sortBy,
            tags_count: selectedTagIds.length
          })}
        </div>
      )}
    </div>
  );
};